// src/analysis/cosechaMecanica/cosechaMecanicaProcess.jsx

import pako from 'pako';
import { toast } from 'react-toastify';
import { CANAL_JOB, esperarJob, lanzarAnalisisEnSegundoPlano, tituloAnalisis } from "../../utils/analisisJobs";

// Líneas de pisoteo de un análisis de Cosecha que ya terminó: el servidor las
// genera en segundo plano a partir de los puntos guardados (sin volver a
// cargar el archivo, sin PDF y sin cobrar hectáreas otra vez).
export const generarLineasPisoteoEnSegundoPlano = async ({
                                                             idAnalisis,
                                                             idUsuario,
                                                             socket,
                                                             harvestLinesBuffer,
                                                             setTramplingLines,
                                                             setShowProgressBar,
                                                             setProgress,
                                                             setTitleLoader,
                                                         }) => {
    if (!idAnalisis) {
        toast.error('Primero ejecuta o abre un análisis de Cosecha Mecánica.', { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
        return false;
    }
    setProgress(0);
    setTitleLoader('Generando líneas de pisoteo en el servidor...');
    setShowProgressBar(true);
    try {
        const respuesta = await lanzarAnalisisEnSegundoPlano({
            tipoAnalisis: 'COSECHA_MECANICA',
            modo: 'NORMAL',
            titulo: 'Líneas de pisoteo · Cosecha Mecánica',
            idUsuario,
            idAnalisis,
            sesionOrigen: null,
            soloCapa: true,
            pasos: [{
                ruta: '/mapping/cosecha_mecanica',
                campos: {
                    id_analisis: String(idAnalisis),
                    coleccion: 'cosecha_mecanica',
                    session_id: CANAL_JOB,
                    generar_lineas: 'true',
                    buffer: String(harvestLinesBuffer ?? 0),
                },
            }],
        });
        setProgress(30);
        const job = await esperarJob(respuesta?.job?.id, { idUsuario, socket });
        if (!job?.urlCapa) throw new Error('El servidor no devolvió líneas para este análisis.');
        setTramplingLines({ url: job.urlCapa });
        setProgress(100);
        toast.success('Líneas de pisoteo listas.', { position: toast.POSITION.TOP_RIGHT, autoClose: 4000 });
        return true;
    } catch (error) {
        console.error('Error al generar las líneas de pisoteo:', error);
        const errorMessage = error?.response?.data?.message || error.message || 'Ocurrió un error, consulta con tu administrador.';
        toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
        return false;
    } finally {
        setShowProgressBar(false);
    }
};

// Análisis principal de Cosecha Mecánica en segundo plano: el servidor hace
// insert_csv y el mapeo aunque se cierre la pestaña.
export const lanzarCosechaMecanicaEnSegundoPlano = async ({
                                                             selectedFile,
                                                             idMax,
                                                             idUsuario,
                                                             socketSessionID,
                                                             setShowProgressBar,
                                                             setProgress,
                                                             setTitleLoader,
                                                             harvestLinesBuffer,
                                                             uiFiltros = {},
                                                         }) => {
    setProgress(0);
    setTitleLoader("Inicio de carga de datos de Cosecha Mecánica...");
    setShowProgressBar(true);

    try {
        const fileBuffer = await selectedFile.arrayBuffer();
        const compressedFile = new Blob([pako.gzip(new Uint8Array(fileBuffer), { level: 9 })], { type: 'application/gzip' });

        setProgress(5);
        setTitleLoader('Enviando archivos al servidor...');
        const respuesta = await lanzarAnalisisEnSegundoPlano({
            tipoAnalisis: 'COSECHA_MECANICA',
            modo: 'NORMAL',
            titulo: tituloAnalisis('COSECHA_MECANICA'),
            idUsuario,
            idAnalisis: idMax,
            sesionOrigen: socketSessionID,
            pasos: [
                {
                    ruta: '/mongo/insert_csv',
                    campos: { collection_name: 'cosecha_mecanica', session_id: CANAL_JOB },
                    archivos: { file: { blob: compressedFile, nombre: `${selectedFile.name}.gz` } },
                },
                {
                    ruta: '/mapping/cosecha_mecanica',
                    campos: {
                        id_analisis: String(idMax),
                        coleccion: 'cosecha_mecanica',
                        session_id: CANAL_JOB,
                        generar_lineas: 'false',
                        buffer: String(harvestLinesBuffer ?? 0),
                    },
                },
            ],
            ui: uiFiltros,
        });

        setProgress(10);
        setTitleLoader('Cosecha Mecánica en procesamiento en el servidor. El mapa se actualizará al finalizar...');
        toast.info(respuesta?.message || 'El análisis de cosecha se está procesando en segundo plano.', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 7000,
        });
    } catch (error) {
        console.error("Error al lanzar Cosecha Mecánica en segundo plano:", error);
        const errorMessage = error?.response?.data?.message || error.message || 'Ocurrió un error, consulta con tu administrador.';
        toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT, autoClose: 5000 });
        setShowProgressBar(false);
    }
};

export const obtenerNumeroLineasArchivo = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const content = e.target.result;
                const lines = content.split(/\r\n|\n/).length;
                resolve(lines);
            } catch (error) {
                console.error("Error al contar las líneas del archivo:", error);
                toast.error('Ocurrió un error, consulta con tu administrador.', {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                reject(error);
            }
        };
        reader.onerror = (error) => {
            console.error("Error al leer el archivo:", error);
            toast.error('Ocurrió un error, consulta con tu administrador.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            reject(error);
        };
        reader.readAsText(file);
    });
};

// Función para ejecutar el proceso de Cosecha Mecánica sin archivo
// Cosecha Mecánica siempre necesita el archivo: el análisis corre en segundo
// plano en el servidor a partir de él.
export const ejecutarProcesoSinArchivoCosechaMecanica = async ({ setShowProgressBar } = {}) => {
    toast.error('Cosecha Mecánica requiere el archivo de la cosechadora para ejecutar el análisis.', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
    });
    if (typeof setShowProgressBar === 'function') setShowProgressBar(false);
};
